import React from 'react';
import clsx from 'clsx';
import { AspectRatio, MediaQuery, ResponsiveImageSpec, generateImageRenditions } from '../../utils/ImageUtils';
import { AImage } from '../../atoms/AImage';
import { AScrimGradient, AScrimGradientDirection } from '../../atoms/AScrimGradient';
import { MButtonIcon, MButtonIconState } from '../MButtonIcon';
import { MLogoContainer } from '../MLogoContainer';
import { MLogoContainerColor } from '../MLogoContainer/MLogoContainer.types';
import { MTag, MTagType } from '../MTag';
import { MMediaImageProps } from './MMediaImage.types';
import * as Styled from './MMediaImage.styles';

const MMEDIA_IMAGE_RENDITION_SPECS: ResponsiveImageSpec[] = [
  { media: MediaQuery.MAX_WIDTH_SM, aspectRatio: AspectRatio.SQUARE, imgWidthPx: 540 },
  { media: MediaQuery.MIN_WIDTH_MD, aspectRatio: AspectRatio.CLASSIC_VER, imgWidthPx: 360 },
  { media: MediaQuery.MIN_WIDTH_LG, aspectRatio: AspectRatio.CLASSIC_HOR, imgWidthPx: 585 },
  { media: MediaQuery.MIN_WIDTH_XL, aspectRatio: AspectRatio.CLASSIC_HOR, imgWidthPx: 750 },
];
const COMPONENT_NAME = 'm-web-ui-kit-MMediaImage';
const TEST_ID = 'web-ui-kit-MMediaImage';
const LOGO_ASPECT_RATIO = '3:2';
const LOGO_WIDTH = 72;
const LOGO_HEIGHT = (LOGO_WIDTH * 2) / 3;
const LOGO_COLOR: MLogoContainerColor = 'white';

export const MMediaImage: React.FC<MMediaImageProps> = ({
  aImage,
  mButtonIcon,
  aScrimGradient: _aScrimGradient,
  mTag,
  mLogoContainer,
  item,
  className,
  renditionSpecs,
  loading = 'lazy',
}) => {
  const mTagObj = typeof mTag === 'object' ? mTag : undefined;
  const mTagStr = typeof mTag === 'string' ? mTag : undefined;

  // NOTE: This resolution is deliberately kept "per field" rather than picking a
  // single source, and it cannot be simplified without a breaking change.
  // `MMediaImage` is already consumed in production (e.g. MMediaCarousel,
  // MMediaSelector) via both discrete props and the legacy `item` prop. The
  // pre-existing behavior already lets a discrete prop win over its `item`
  // counterpart per field, so callers may already rely on mixing the two (pass
  // `item` but override a single prop). This resolution is kept as-is to
  // preserve that behavior; collapsing onto one source, or dropping `item`,
  // would silently change behavior for those existing consumers.
  const resolvedImage = aImage?.image ?? item?.aImage?.image;
  const resolvedButtonIcon = mButtonIcon ?? item?.mButtonIcon;
  const resolvedLogo = mLogoContainer ?? item?.mLogoContainer;

  const tagText = mTagObj?.aLabel?.text ?? item?.mTag?.aLabel?.text ?? mTagStr ?? item?.tag ?? '';
  const tagType = mTagObj?.type ?? MTagType.Inverse;
  const tagIcon = mTagObj?.aIcon;
  const specs = renditionSpecs ?? MMEDIA_IMAGE_RENDITION_SPECS;
  const mButtonIconData = resolvedButtonIcon?.aIcon?.icon ? resolvedButtonIcon : undefined;
  const logoBrand = resolvedLogo?.aLogo32?.brand;
  const logoPath = resolvedLogo?.aLogo32?.logopath;
  const logoAlignment = resolvedLogo?.aLogo32?.alignment;
  const showLogo = !!(logoBrand || logoPath);

  return (
    <Styled.Wrapper className={clsx('inverse', className)} data-component-name={COMPONENT_NAME} data-testid={TEST_ID}>
      {showLogo && (
        <Styled.ScrimWrapper>
          <AScrimGradient direction={AScrimGradientDirection.BottomToTop} />
        </Styled.ScrimWrapper>
      )}
      {resolvedImage && (
        <AImage
          image={resolvedImage}
          renditions={generateImageRenditions(specs, resolvedImage.assetPath)}
          loading={aImage?.loading ?? loading}
          fetchpriority={aImage?.fetchpriority}
        />
      )}
      {mButtonIconData && (
        <Styled.ButtonIconWrapper>
          <MButtonIcon
            state={MButtonIconState.Transparent}
            {...mButtonIconData}
            aria-label={mButtonIconData['aria-label']?.trim() || 'Expand image'}
          />
        </Styled.ButtonIconWrapper>
      )}
      {showLogo && (
        <Styled.LogoWrapper>
          <MLogoContainer
            brand={logoBrand ?? ''}
            aspectRatio={LOGO_ASPECT_RATIO}
            width={LOGO_WIDTH}
            height={LOGO_HEIGHT}
            color={LOGO_COLOR}
            logopath={logoPath}
            alignment={logoAlignment}
          />
        </Styled.LogoWrapper>
      )}
      {tagText && (
        <Styled.TagWrapper>
          <MTag aLabel={{ text: tagText }} type={tagType} aIcon={tagIcon} />
        </Styled.TagWrapper>
      )}
    </Styled.Wrapper>
  );
};
